import { z } from 'zod';
import i18next from 'i18next';
import { http } from '@/services/http';
import { ROUTES } from '@/app/routes';

// ── Caixa inteligente — GET /users/me/hexis/inbox ────────────────────────────
// A tela de notificações é servida pelo CÉREBRO, não pela tabela: o Hexis tria
// (pulse → medula), explica (why) e resume (briefing). Aqui só desembrulhamos.

const itemSchema = z.object({
  id: z.string(),
  type: z.string().nullish(),          // match | event | xp | connection | content | alert | hexis | system | mission
  title: z.string().nullish(),
  body: z.string().nullish(),
  text: z.string().nullish(),
  time: z.string().nullish(),          // relativo, já formatado pela API (pt-PT)
  read: z.boolean().nullish(),
  created_at: z.string().nullish(),
  target: z.object({ kind: z.string().nullish(), id: z.string().nullish() }).nullish(),
  // Campos do Hexis (a inteligência que a tela mostra):
  score: z.number().nullish(),         // relevância decidida pela medula
  why: z.string().nullish(),           // porquê agora — explicabilidade
  priority: z.string().nullish(),      // high | normal | low
  cta_label: z.string().nullish(),
  from_hexis: z.boolean().nullish(),   // nasceu do /hexis/decide
}).passthrough();
export type InboxItem = z.infer<typeof itemSchema>;

export interface HexisInbox {
  briefing: { text: string; focus: string | null } | null;
  pulse: {
    first_name?: string | null; level?: number | null; connections?: number | null;
    tickets?: number | null; days_since_seen?: number | null; churn_risk?: boolean | null;
  } | null;
  lanes: { agir_agora: InboxItem[]; para_ti: InboxItem[]; historico: InboxItem[] };
  counts: { total: number; unread: number; agir_agora: number; para_ti: number; historico: number };
  hexis: { online: boolean; cortex: boolean; medula: boolean; reason?: string | null };
}

// Um item malformado NUNCA pode zerar a caixa (ver bug conhecido do .parse() em
// listas): validamos item-a-item e descartamos só o que está partido.
function parseLane(raw: unknown): InboxItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((r) => {
    const p = itemSchema.safeParse(r);
    return p.success ? [p.data] : [];
  });
}

const EMPTY_LANES = { agir_agora: [], para_ti: [], historico: [] };
const EMPTY_COUNTS = { total: 0, unread: 0, agir_agora: 0, para_ti: 0, historico: 0 };

/** A tela é 100% dependente do Hexis: se o cérebro não responde, dizemo-lo — não
 *  há degradação silenciosa para uma lista burra. */
export async function getHexisInbox(): Promise<HexisInbox> {
  try {
    const raw = await http.get<Record<string, unknown>>('/users/me/hexis/inbox');
    const lanes = (raw?.lanes ?? {}) as Record<string, unknown>;
    const h = (raw?.hexis ?? {}) as Record<string, unknown>;
    return {
      briefing: (raw?.briefing ?? null) as HexisInbox['briefing'],
      pulse: (raw?.pulse ?? null) as HexisInbox['pulse'],
      lanes: {
        agir_agora: parseLane(lanes.agir_agora),
        para_ti: parseLane(lanes.para_ti),
        historico: parseLane(lanes.historico),
      },
      counts: { ...EMPTY_COUNTS, ...(raw?.counts as object ?? {}) },
      hexis: {
        online: h.online !== false,
        cortex: !!h.cortex,
        medula: h.medula !== false,
        reason: (h.reason as string) ?? null,
      },
    };
  } catch {
    return { briefing: null, pulse: null, lanes: EMPTY_LANES, counts: EMPTY_COUNTS,
      hexis: { online: false, cortex: false, medula: false, reason: i18next.t('notif.noLink', { ns: 'hexis' }) } };
  }
}

/** Destino real de cada notificação. Só ROUTES (nunca paths à mão) — o CTA leva
 *  o utilizador ao sítio concreto de que a notificação fala. */
export function deepLink(n: InboxItem): string | null {
  const t = n.target;
  if (t?.id) {
    if (t.kind === 'event') return ROUTES.attendee.event(t.id);
    if (t.kind === 'user') return ROUTES.tap.profile(t.id);
  }
  switch (n.type) {
    case 'connection': return ROUTES.tap.connections;
    case 'match':      return ROUTES.tap.root;
    case 'event':      return ROUTES.attendee.explore;
    case 'content':    return ROUTES.conteudos;
    case 'xp':
    case 'mission':    return ROUTES.conta;
    case 'hexis':      return ROUTES.hexis;
    default:           return null;
  }
}
